import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { X, RotateCw, History } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import ErrorNote from "./ErrorNote";
import StatusPill from "./StatusPill";

// Migration History modal — the last 20 tenant_db_migrations rows for one
// database, with a Retry on the failed ones. Opened from the row menu, and
// reachable even when the database itself could not be read: that is exactly
// when its history is the thing you need.
const PAGE_SIZE = 20;

const MigrationHistoryModal = ({ row, onClose, onRetryDone }) => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [retryingId, setRetryingId] = useState(null);

    const load = useCallback(async () => {
        if (!row?.tenantId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await PlatformService.fetchTenantMigrations(row.tenantId, {
                page: 1,
                per_page: PAGE_SIZE,
            });
            setItems(data?.data?.items ?? []);
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setLoading(false);
        }
    }, [row?.tenantId]);

    useEffect(() => {
        load();
    }, [load]);

    const onRetry = async (mig) => {
        if (retryingId) return;
        try {
            setRetryingId(mig.id);
            const res = await PlatformService.retryTenantMigration(row.tenantId, mig.id);
            if (res?.success) {
                toast.success("The retry succeeded.");
            } else {
                toast.error(res?.error || "The retry failed. The reason is on the new row below.");
            }
            await load();
            onRetryDone?.();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setRetryingId(null);
        }
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && !retryingId && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[820px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        Migration history
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
                        {row?.label || row?.tenantSlug || "—"} · the last {PAGE_SIZE}{" "}
                        attempts, newest first
                    </DialogDescription>
                    <DialogClose
                        disabled={!!retryingId}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4">
                    {loading && (
                        <div className="space-y-2">
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    )}

                    {!loading && error && <ErrorNote>{error}</ErrorNote>}

                    {!loading && !error && items.length === 0 && (
                        <div className="flex flex-col items-center gap-2 py-10 text-center">
                            <History size={20} aria-hidden="true" className="text-text-muted" />
                            <div className="text-[13px] font-medium text-text-primary">
                                Nothing has run against this database yet
                            </div>
                            <div className="text-[11px] text-text-secondary max-w-md">
                                Every schema upgrade and row-security apply lands here,
                                whether it succeeds or fails.
                            </div>
                        </div>
                    )}

                    {!loading && !error && items.length > 0 && (
                        <div className="rounded-md border border-border-primary divide-y divide-border-primary text-[12px]">
                            {items.map((m) => (
                                <div key={m.id} className="px-3 py-2 flex items-start gap-3">
                                    <StatusPill status={m.status} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-text-primary uppercase text-[11px] tracking-wide">
                                                {m.kind}
                                            </span>
                                            <span className="text-text-muted">·</span>
                                            <span className="text-text-secondary">
                                                {(m.direction || "upgrade").replace(":", " → ")}
                                            </span>
                                            {m.triggeredBy && (
                                                <>
                                                    <span className="text-text-muted">·</span>
                                                    <span className="text-text-secondary">
                                                        {m.triggeredBy}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className="text-text-secondary mt-0.5 font-mono truncate"
                                            title={`${m.fromRevision || "?"} → ${m.toRevision || "?"}`}
                                        >
                                            {(m.fromRevision || "?").slice(0, 12)} →{" "}
                                            {(m.toRevision || "?").slice(0, 12)}
                                        </div>
                                        <div className="text-text-muted mt-0.5">
                                            {formatStamp(m.startedAt)}
                                            {m.durationMs != null &&
                                                ` · ${(m.durationMs / 1000).toFixed(1)}s`}
                                        </div>
                                        {m.errorText && (
                                            <div className="text-tone-rose mt-1 whitespace-pre-wrap break-words">
                                                {m.errorText}
                                            </div>
                                        )}
                                    </div>
                                    {m.status === "failed" && (
                                        <Button
                                            onClick={() => onRetry(m)}
                                            disabled={!!retryingId}
                                            className="bg-background-icon text-text-primary hover:bg-background-icon/60 text-[11px] shrink-0"
                                        >
                                            <RotateCw
                                                size={12}
                                                aria-hidden="true"
                                                className={`mr-1 ${
                                                    retryingId === m.id ? "animate-spin" : ""
                                                }`}
                                            />
                                            {retryingId === m.id ? "Retrying…" : "Retry"}
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-between items-center gap-2">
                    <Button
                        variant="outline"
                        type="button"
                        onClick={load}
                        disabled={loading || !!retryingId}
                    >
                        {loading ? "Refreshing…" : "Refresh"}
                    </Button>
                    <DialogClose
                        disabled={!!retryingId}
                        render={
                            <Button variant="outline" type="button" disabled={!!retryingId}>
                                Close
                            </Button>
                        }
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

// The server sends UTC with an offset. Two applies in one day are the normal
// case here, so the time matters as much as the date — `formatDate` drops it.
// This is the same format the Migrations tab under Audit Logs uses.
const formatStamp = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default MigrationHistoryModal;
